import { useCampaignCode } from '@campaign-services/hooks/useCampaignCode'
import { COSIGN_TOKEN_KEY, ERROR_CODES, type ProjectCode } from '@constants'
import { type ResponseErrorGenerics } from '@dni-types/utilities/response'
import { useApiCampaignActivityInfo } from '@hooks-api/fortune/useApiCampaignActivityInfo'
import { useApiTokenLogin } from '@hooks-api/fortune/useApiTokenLogin'
import { Button } from '@pets-ui/Button/Button'
import { NoticeOutlined } from '@pets-ui/icons'
import { Text } from '@pets-ui/Text/Text'
import { colors } from '@pets-ui/tokens/colors.stylex'
import { VStack } from '@pets-ui/VStack/VStack'
import { type AxiosError } from 'axios'
import Cookies from 'js-cookie'
import { jwtDecode } from 'jwt-decode'
import queryString from 'query-string'
import { useMemo, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom-v5'
import { getClient } from '../../clients'
import Loading from '../../components/loading'
import { merchantCustomerServiceLineMap } from './MerchantLogin/linkMaps'

export function AuthProcessing() {
  const { campaignCodeFromUrl } = useCampaignCode()
  const { search } = useLocation()
  const history = useHistory()
  const { destructuredData: exhibitorSettings } = useApiCampaignActivityInfo(campaignCodeFromUrl)
  const project = exhibitorSettings?.brand_codes_info[0]?.project_code
  const [errorCode, setErrorCode] = useState('')
  const errorMessageMap = useMemo<Record<string, string>>(() => ({
    [ERROR_CODES.LOGIN_ERROR_NO_SALES_ROLE]: '登入異常，帳號無經銷角色，請洽負責業務',
    [ERROR_CODES.LOGIN_ERROR_PARTNER_STATUS_ERROR]: `登入異常，合作狀態異常請聯繫負責業務或官方LINE ${merchantCustomerServiceLineMap[project as ProjectCode]}`,
    [ERROR_CODES.LOGIN_ERROR_PARTNER_REGION_ERROR]: `此站台不符合您的合作地區，請聯繫負責業務或官方LINE ${merchantCustomerServiceLineMap[project as ProjectCode]}`,
    default: '權限不足，無法登入',
  }), [project])
  const queryParams = queryString.parse(search)
  const { isLoading: isTokenLoginLoading, destructuredData } = useApiTokenLogin({
    campaign_code: campaignCodeFromUrl,
    cosign_token: queryParams.token as string,
  }, {
    onSuccess: ({ data }) => {
      const cosignToken = data.data.access_token
      Cookies.set(`${campaignCodeFromUrl}_${COSIGN_TOKEN_KEY}`, cosignToken)
      getClient('fortune').defaults.headers['access-token'] = cosignToken
      getClient('butler').defaults.headers.Authorization = `Bearer ${cosignToken}`
      const jwtPayload = jwtDecode<{ redirectUrl?: string }>(queryParams.token as string)
      window.location.href = jwtPayload.redirectUrl ?? `${window.location.origin}/merchant/${campaignCodeFromUrl}/product-catalog`
    },
    onError: (error) => {
      const errorData = (error as AxiosError<{ error: ResponseErrorGenerics }>).response?.data.error
      setErrorCode(String(errorData?.code) ?? '')
    },
    onErrorRetry: (error) => {
      // NOTE: Never retry on 403.
      if (error.status === 403) return null
    },
  })

  if (isTokenLoginLoading || destructuredData) {
    return <Loading />
  }

  const handleClickBack = () => {
    history.push(`/merchant/${campaignCodeFromUrl}/login`)
  }

  return (
    <VStack gap={5} paddingTop="212px" alignItems="center" justifyContent="center">
      <NoticeOutlined color={colors.red600} fontSize="80px" />
      <Text variant="h4">{errorMessageMap[errorCode] || errorMessageMap.default}</Text>
      <Button width="350px" onClick={handleClickBack}>返回登入頁</Button>
    </VStack>
  )
}
